import React, { useState, useRef, useEffect } from 'react'
import { CREDIT_PRICES, addUsageCredits } from '../usage'

async function getRealInsightResponse(userMessage) {
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt: userMessage })
  })
  const data = await response.json()
  return data.result || data.content || 'No response received.'
}

// ─── Simple markdown-ish renderer ────────────────────────────────────────────

function renderAnswer(text) {
  const lines = text.split('\n')
  return lines.map((line, i) => {
    // Table rows
    if (line.startsWith('|')) {
      const cells = line.split('|').filter(c => c.trim() !== '')
      const isHeader = lines[i + 1] && lines[i + 1].startsWith('|---')
      const isSeparator = /^\|[-| ]+\|$/.test(line)
      if (isSeparator) return null
      return (
        <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
          {cells.map((cell, j) => {
            const Tag = isHeader ? 'th' : 'td'
            return (
              <Tag key={j} style={{
                padding: '6px 10px', textAlign: 'left',
                fontSize: '12px', fontFamily: "'Inter', sans-serif",
                fontWeight: isHeader ? '700' : '400',
                color: isHeader ? '#6b7280' : '#374151',
                background: isHeader ? '#f8fafd' : 'transparent',
              }} dangerouslySetInnerHTML={{ __html: cell.trim().replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            )
          })}
        </tr>
      )
    }
    return null
  }).some(x => x !== null) ? (
    // Has table rows — wrap them
    <div key="table" style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid rgba(29,111,219,0.12)', borderRadius: '8px', overflow: 'hidden' }}>
        <tbody>
          {lines.map((line, i) => {
            if (!line.startsWith('|')) return null
            const isSeparator = /^\|[-| ]+\|$/.test(line)
            if (isSeparator) return null
            const cells = line.split('|').filter(c => c.trim() !== '')
            const isHeader = lines[i + 1] && lines[i + 1].startsWith('|---')
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
                {cells.map((cell, j) => {
                  const Tag = isHeader ? 'th' : 'td'
                  return (
                    <Tag key={j} style={{
                      padding: '6px 10px', textAlign: 'left',
                      fontSize: '12px', fontFamily: "'Inter', sans-serif",
                      fontWeight: isHeader ? '700' : '400',
                      color: isHeader ? '#6b7280' : '#374151',
                      background: isHeader ? '#f8fafd' : 'transparent',
                    }} dangerouslySetInnerHTML={{ __html: cell.trim().replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  ) : (
    // Plain lines
    <div>
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />
        const html = line
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/^• /, '&bull; ')
        return (
          <p key={i} style={{ margin: '0 0 4px', fontSize: '13px', lineHeight: '1.65', fontFamily: "'Inter', sans-serif", color: '#374151' }}
            dangerouslySetInnerHTML={{ __html: html }} />
        )
      })}
    </div>
  )
}

// ─── Suggested questions ───────────────────────────────────────────────────────

const SUGGESTIONS = [
  'How are ticket sales trending vs last year?',
  'Who are my biggest donors this season?',
  'What\'s my subscription renewal rate?',
  'Which performances are selling best?',
]

// ─── Main component ───────────────────────────────────────────────────────────

function AiInsights({ embedded, onUse, orgData, onUsageUpdated }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const recordAiReplyUsage = async () => {
    try {
      await addUsageCredits(orgData, CREDIT_PRICES.aiChatReply)
      if (onUsageUpdated) await onUsageUpdated()
    } catch (err) {
      console.error('Usage update failed:', err)
    }
  }

  const sendMessage = async (text) => {
    const userText = (text || input).trim()
    if (!userText || loading) return

    setInput('')
    setMessages(prev => [...prev, { role: 'user', text: userText }])
    setLoading(true)

    try {
      const response = await getRealInsightResponse(userText)
      await recordAiReplyUsage()
      setMessages(prev => [...prev, { role: 'assistant', text: response }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Something went wrong. Try again.' }])
    }
    setLoading(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const isEmpty = messages.length === 0

  return (
    <div>
      {!embedded && (
        <div style={s.header}>
          <h1 style={s.title}>Insights</h1>
          <p style={s.subtitle}>Ask anything about your season performance, donor trends, or sales figures — in plain English.</p>
        </div>
      )}

      <div style={s.panel}>
        <div style={s.chatWrapper}>
          <div style={s.chatMessages}>
            {isEmpty && (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>💬</div>
                <p style={s.emptyTitle}>Ask a question about your data</p>
                <p style={s.emptyHint}>Sales pacing, donor trends, subscription rates, top performances — just ask.</p>
                <div style={s.suggestions}>
                  {SUGGESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      style={s.suggestionBtn}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} style={msg.role === 'user' ? s.userRow : s.assistantRow}>
                {msg.role === 'user' ? (
                  <div style={{ ...s.bubble, ...s.userBubble }}>
                    <p style={s.bubbleText}>{msg.text}</p>
                  </div>
                ) : (
                  <div style={{ ...s.bubble, ...s.assistantBubble }}>
                    {renderAnswer(msg.text)}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div style={s.assistantRow}>
                <div style={{ ...s.bubble, ...s.assistantBubble }}>
                  <div style={s.typing}>
                    <span style={s.dot}>●</span>
                    <span style={{ ...s.dot, animationDelay: '0.2s' }}>●</span>
                    <span style={{ ...s.dot, animationDelay: '0.4s' }}>●</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          <div style={s.inputArea}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your sales, donors, or performance trends…"
              style={s.chatInput}
              rows={1}
              disabled={loading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                ...s.sendBtn,
                ...((!input.trim() || loading) ? s.sendBtnDisabled : {}),
              }}
            >
              ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  subtitle: { fontSize: '13px', color: '#4b5563', fontFamily: "'Inter', sans-serif", maxWidth: '560px', lineHeight: '1.6' },

  panel: {
    backgroundColor: 'white', borderRadius: '12px', padding: '20px',
    boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)',
    maxWidth: '760px',
  },

  chatWrapper: { display: 'flex', flexDirection: 'column', height: '520px' },

  chatMessages: {
    flex: 1, overflowY: 'auto', padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: '10px',
    backgroundColor: '#fafbfd', borderRadius: '10px',
    border: '1px solid rgba(29,111,219,0.1)', marginBottom: '10px',
  },

  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: '#9ca3af', textAlign: 'center', padding: '20px',
  },
  emptyIcon: { fontSize: '24px', color: '#c7d9f5', marginBottom: '8px' },
  emptyTitle: { fontSize: '13px', fontWeight: '600', color: '#9ca3af', marginBottom: '4px', fontFamily: "'Inter', sans-serif" },
  emptyHint: { fontSize: '12px', color: '#b0bac7', lineHeight: '1.5', maxWidth: '320px', fontFamily: "'Inter', sans-serif", marginBottom: '16px' },

  suggestions: { display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', maxWidth: '460px' },
  suggestionBtn: {
    padding: '8px 14px', background: 'rgba(29,111,219,0.06)', border: '1px solid rgba(29,111,219,0.15)',
    borderRadius: '8px', fontSize: '12px', color: '#1d6fdb', fontFamily: "'Inter', sans-serif",
    cursor: 'pointer', textAlign: 'left', fontWeight: '500',
  },

  userRow: { display: 'flex', justifyContent: 'flex-end' },
  assistantRow: { display: 'flex', justifyContent: 'flex-start' },

  bubble: { maxWidth: '85%', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', lineHeight: '1.6', border: '1px solid #edf0f5' },
  userBubble: { backgroundColor: '#1d6fdb', color: 'white', border: 'none', boxShadow: '0 1px 4px rgba(29,111,219,0.25)' },
  assistantBubble: { backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  bubbleText: { margin: 0, whiteSpace: 'pre-wrap', fontFamily: "'Inter', sans-serif" },

  typing: { display: 'flex', gap: '4px', padding: '2px 0' },
  dot: { fontSize: '8px', color: '#9ca3af', animation: 'blink 1.4s infinite both', fontFamily: 'monospace' },

  inputArea: {
    display: 'flex', alignItems: 'flex-end', gap: '8px',
    padding: '8px 0 0',
  },
  chatInput: {
    flex: 1, padding: '9px 13px', border: '1px solid rgba(29,111,219,0.18)',
    borderRadius: '8px', fontSize: '13px', fontFamily: "'Inter', sans-serif",
    resize: 'none', outline: 'none', lineHeight: '1.5', maxHeight: '100px',
    backgroundColor: '#fafbfd',
  },
  sendBtn: {
    width: '34px', height: '34px', borderRadius: '8px',
    backgroundColor: '#1d6fdb', color: 'white', border: 'none',
    fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sendBtnDisabled: { backgroundColor: '#d1d9e6', cursor: 'not-allowed' },
}

// Inject blink keyframe once
const _styleTag = document.createElement('style')
_styleTag.textContent = `@keyframes blink { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }`
document.head.appendChild(_styleTag)

export default AiInsights
